import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function OdooActivityMonitor({ user }) {
  const { activityId } = useParams();
  const navigate = useNavigate();

  const [activity, setActivity]     = useState(null);
  const [results, setResults]       = useState([]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openingFor, setOpeningFor] = useState({}); // { student_id: true }
  const [expanded, setExpanded]     = useState({}); // { student_id: true }
  const [error, setError]           = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Google Classroom states
  const [showClassroomModal, setShowClassroomModal] = useState(false);
  const [courses, setCourses] = useState([]);
  const [courseworks, setCourseworks] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedCoursework, setSelectedCoursework] = useState('');
  const [submittingGrades, setSubmittingGrades] = useState(false);
  const [classroomMessage, setClassroomMessage] = useState(null);
  const [creatingAssignment, setCreatingAssignment] = useState(false);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const [actRes, resRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/odoo-activities/${activityId}`),
        axios.get(`${BACKEND_URL}/api/odoo-activities/${activityId}/results`)
      ]);
      setActivity(actRes.data);
      setResults(resRes.data.results || []);
      setStats(resRes.data.statistics || null);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError('Error al cargar datos de la actividad');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activityId]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const openStudentOdoo = async (studentId, studentName) => {
    setOpeningFor(prev => ({ ...prev, [studentId]: true }));
    try {
      const response = await axios.post(`${BACKEND_URL}/api/odoo-activities/start-container`, {
        student_id: studentId,
        activity_id: activityId
      });
      const url = response.data.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      alert(`Error al abrir Odoo de ${studentName}:\n${err.response?.data?.detail || err.message}`);
    } finally {
      setOpeningFor(prev => ({ ...prev, [studentId]: false }));
    }
  };

  const toggleExpanded = (studentId) =>
    setExpanded(prev => ({ ...prev, [studentId]: !prev[studentId] }));

  const exportResults = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/odoo-activities/export/csv?activity_id=${activityId}&best_score_only=true`,
        { responseType: 'blob' }
      );
      
      // Crear blob y descargar
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `calificaciones_${activity?.title?.replace(/[^a-zA-Z0-9]/g, '_') || 'odoo'}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting results:', err);
      alert('Error al exportar las calificaciones');
    }
  };

  // Google Classroom functions
  const [classroomConnected, setClassroomConnected] = useState(null);

  const checkClassroomConnection = async () => {
    try {
      // Intentar cargar cursos - si falla con 404, el profesor no está conectado
      const response = await axios.get(
        `${BACKEND_URL}/api/classroom/courses/${user?.id}`
      );
      setClassroomConnected(true);
      setCourses(response.data.courses || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setClassroomConnected(false);
        setClassroomMessage({ 
          type: 'error', 
          text: 'No estás conectado a Google Classroom. Conecta primero desde el panel del profesor.' 
        });
      } else {
        setClassroomMessage({ type: 'error', text: 'Error al cargar los cursos de Classroom' });
      }
    }
  };

  const fetchCourseworks = async (courseId) => {
    if (!courseId) return;
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/classroom/course/${courseId}/courseworks/${user?.id}`
      );
      setCourseworks(response.data.courseworks || []);
    } catch (err) {
      console.error('Error fetching courseworks:', err);
      setCourseworks([]);
    }
  };

  const openClassroomModal = () => {
    setShowClassroomModal(true);
    setClassroomMessage(null);
    setClassroomConnected(null);
    checkClassroomConnection();
  };

  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    setSelectedCourse(courseId);
    setSelectedCoursework('');
    fetchCourseworks(courseId);
  };

  const submitAllGrades = async () => {
    if (!selectedCourse || !selectedCoursework) {
      setClassroomMessage({ type: 'error', text: 'Selecciona un curso y una tarea' });
      return;
    }

    setSubmittingGrades(true);
    setClassroomMessage(null);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/odoo-activities/${activityId}/submit-all-grades`,
        {
          teacher_id: user?.id,
          course_id: selectedCourse,
          coursework_id: selectedCoursework
        }
      );

      setClassroomMessage({
        type: 'success',
        text: `✅ ${response.data.message}`
      });
      
      // Refresh data to show submitted status
      fetchData(true);
    } catch (err) {
      console.error('Error submitting grades:', err);
      setClassroomMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Error al enviar las calificaciones'
      });
    } finally {
      setSubmittingGrades(false);
    }
  };

  const createClassroomAssignment = async () => {
    if (!selectedCourse) {
      setClassroomMessage({ type: 'error', text: 'Selecciona un curso primero' });
      return;
    }

    setCreatingAssignment(true);
    setClassroomMessage(null);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/odoo-activities/${activityId}/create-classroom-assignment`,
        {
          teacher_id: user?.id,
          course_id: selectedCourse
        }
      );

      setClassroomMessage({
        type: 'success',
        text: `✅ Tarea creada en Classroom. ID: ${response.data.coursework_id}`
      });
      
      // Refresh courseworks
      fetchCourseworks(selectedCourse);
    } catch (err) {
      console.error('Error creating assignment:', err);
      setClassroomMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Error al crear la tarea en Classroom'
      });
    } finally {
      setCreatingAssignment(false);
    }
  };

  const maxScore = activity?.objectives?.reduce((s, o) => s + (o.score || 0), 0) || 100;

  // Estados para gestión de módulos
  const [moduleStatus, setModuleStatus] = useState(null);
  const [checkingModule, setCheckingModule] = useState(false);
  const [updatingAppsList, setUpdatingAppsList] = useState({}); // { student_id: true }
  const [verifyingStudent, setVerifyingStudent] = useState({}); // { student_id: true }

  // Verificar estado del módulo en todos los alumnos
  const checkModuleStatus = async () => {
    setCheckingModule(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/odoo-activities/${activityId}/module-status`
      );
      setModuleStatus(response.data);
    } catch (err) {
      console.error('Error checking module status:', err);
      alert('Error al verificar el estado del módulo: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCheckingModule(false);
    }
  };

  // Verificar actividad de un alumno específico
  const verifyStudentActivity = async (studentId, studentName) => {
    setVerifyingStudent(prev => ({ ...prev, [studentId]: true }));
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/odoo-activities/${activityId}/verify`,
        {
          student_id: studentId,
          student_name: studentName
        }
      );
      
      const result = response.data;
      alert(`✅ Verificación completada para ${studentName}\n\n` +
        `Puntuación: ${result.summary.score}/${result.summary.max_score}\n` +
        `Porcentaje: ${result.summary.percentage}%\n` +
        `Estado: ${result.summary.passed ? '✅ Aprobado' : '⏳ En progreso'}\n` +
        `Objetivos completados: ${result.summary.objectives_completed}/${result.summary.total_objectives}`
      );
      
      // Refrescar datos para mostrar el nuevo resultado
      fetchData(true);
    } catch (err) {
      console.error('Error verifying student:', err);
      const errorMsg = err.response?.data?.detail || err.message;
      alert(`❌ Error al verificar a ${studentName}:\n${errorMsg}`);
    } finally {
      setVerifyingStudent(prev => ({ ...prev, [studentId]: false }));
    }
  };

  // Actualizar lista de aplicaciones para un alumno específico
  const updateAppsListForStudent = async (studentId, studentName) => {
    setUpdatingAppsList(prev => ({ ...prev, [studentId]: true }));
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/student/${studentId}/update-apps-list`
      );
      
      const data = response.data;
      let message = `Lista de aplicaciones actualizada para ${studentName}.\n\n`;
      
      if (data.found_modules && data.found_modules.length > 0) {
        message += `Módulos encontrados:\n`;
        data.found_modules.forEach(mod => {
          message += `- ${mod.title} (${mod.state})\n`;
        });
      } else {
        message += `No se encontraron módulos pre-cargados.\n`;
      }
      
      message += `\n${data.modules_count.new} módulos nuevos detectados.`;
      
      alert(message);
      
      // Refrescar estado si está abierto
      if (moduleStatus) {
        checkModuleStatus();
      }
    } catch (err) {
      console.error('Error updating apps list:', err);
      alert('Error al actualizar lista de aplicaciones: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUpdatingAppsList(prev => ({ ...prev, [studentId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 p-4">
      <div className="max-w-6xl mx-auto space-y-5">

        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">
              🏢 {activity?.title || 'Actividad Odoo'}
            </h1>
            <p className="text-white/60 text-sm">
              {activity?.objectives?.length} objetivos · {maxScore} pts máx ·{' '}
              {activity?.difficulty === 'beginner' ? 'Iniciación'
                : activity?.difficulty === 'intermediate' ? 'Intermedio' : 'Avanzado'}
              {lastUpdate && (
                <span className="ml-3">
                  · Actualizado: {lastUpdate.toLocaleTimeString()}
                  {refreshing && <span className="ml-1 animate-pulse">🔄</span>}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all text-sm disabled:opacity-50"
            >
              🔄 Actualizar
            </button>
            <button
              onClick={exportResults}
              className="px-4 py-2 bg-green-600/80 hover:bg-green-600 text-white rounded-xl transition-all text-sm"
            >
              📥 CSV
            </button>
            <button
              onClick={openClassroomModal}
              className="px-4 py-2 bg-blue-600/80 hover:bg-blue-600 text-white rounded-xl transition-all text-sm"
            >
              🎓 Classroom
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all text-sm"
            >
              ← Volver
            </button>
            <button
              onClick={checkModuleStatus}
              disabled={checkingModule}
              className="px-4 py-2 bg-purple-600/80 hover:bg-purple-600 text-white rounded-xl transition-all text-sm disabled:opacity-50"
              title="Verificar estado del módulo de backup en todos los alumnos"
            >
              {checkingModule ? '⏳ Verificando...' : '💾 Estado Módulos'}
            </button>
          </div>
        </div>

        {/* Module Status Panel */}
        {moduleStatus && (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-bold text-white">Estado del Módulo: Database Ultimate Backup Lite</h2>
                <p className="text-white/60 text-sm">
                  Total alumnos: {moduleStatus.total_students} | 
                  Con contenedor: {moduleStatus.summary.with_container} | 
                  Instalado: {moduleStatus.summary.module_installed} | 
                  Disponible: {moduleStatus.summary.module_available} | 
                  Sin contenedor: {moduleStatus.summary.no_container}
                </p>
              </div>
              <button
                onClick={() => setModuleStatus(null)}
                className="text-white/60 hover:text-white"
              >
                ✕ Cerrar
              </button>
            </div>
            
            {moduleStatus.students && moduleStatus.students.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white/60 border-b border-white/10">
                      <th className="text-left py-2">Alumno</th>
                      <th className="text-center py-2">Contenedor</th>
                      <th className="text-center py-2">Estado Módulo</th>
                      <th className="text-right py-2">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {moduleStatus.students.map((student) => (
                      <tr key={student.student_id} className="border-b border-white/5">
                        <td className="py-2 text-white">
                          {student.student_name}
                          {student.student_email && (
                            <div className="text-white/50 text-xs">{student.student_email}</div>
                          )}
                        </td>
                        <td className="text-center py-2">
                          {student.has_container ? (
                            <span className="text-green-400">✓ Sí (puerto {student.container_port})</span>
                          ) : (
                            <span className="text-red-400">✗ No</span>
                          )}
                        </td>
                        <td className="text-center py-2">
                          {student.module_installed ? (
                            <span className="px-2 py-1 bg-green-500/30 text-green-300 rounded text-xs">Instalado</span>
                          ) : student.module_state === 'uninstalled' || student.module_state === 'not_found' ? (
                            <span className="px-2 py-1 bg-yellow-500/30 text-yellow-300 rounded text-xs">
                              {student.module_state === 'not_found' ? 'No detectado' : 'No instalado'}
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-500/30 text-gray-300 rounded text-xs">{student.module_state}</span>
                          )}
                        </td>
                        <td className="text-right py-2">
                          {student.has_container && !student.module_installed && (
                            <button
                              onClick={() => updateAppsListForStudent(student.student_id, student.student_name)}
                              disabled={updatingAppsList[student.student_id]}
                              className="px-3 py-1 bg-blue-600/80 hover:bg-blue-600 text-white rounded text-xs transition-all disabled:opacity-50"
                            >
                              {updatingAppsList[student.student_id] ? '⏳' : '🔄 Actualizar Apps'}
                            </button>
                          )}
                          {student.web_url && (
                            <a
                              href={student.web_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 px-3 py-1 bg-white/20 hover:bg-white/30 text-white rounded text-xs transition-all"
                            >
                              Abrir Odoo
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-blue-200 text-sm">
                <strong>💡 Instrucciones:</strong> Los alumnos deben ir a <em>Aplicaciones</em> en su Odoo, buscar "Database Ultimate Backup Lite" y hacer clic en <em>Instalar</em>. 
                Si el módulo no aparece, haz clic en "Actualizar Apps" para ese alumno.
              </p>
            </div>
          </div>
        )}

        {/* Stats */}
        {stats && stats.total_students > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Alumnos', value: stats.total_students, color: 'text-blue-300' },
              {
                label: 'Aprobados',
                value: `${stats.passed} (${stats.total_students ? Math.round(stats.passed / stats.total_students * 100) : 0}%)`,
                color: 'text-green-300'
              },
              { label: 'Nota media', value: `${stats.average_score}/${maxScore}`, color: 'text-yellow-300' },
              { label: 'Intentos medios', value: stats.average_attempts, color: 'text-purple-300' },
            ].map(s => (
              <div key={s.label} className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20 text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-white/60 text-xs mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-200 p-4 rounded-xl">
            {error}
          </div>
        )}

        {/* Results table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {results.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-5xl mb-3">📭</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-1">Sin verificaciones aún</h3>
              <p className="text-gray-500 text-sm">
                Los alumnos aparecerán aquí cuando pulsen "Verificar progreso".
              </p>
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-gray-600 font-semibold w-10">#</th>
                  <th className="px-4 py-3 text-gray-600 font-semibold">Alumno</th>
                  <th className="px-4 py-3 text-gray-600 font-semibold">Nota</th>
                  <th className="px-4 py-3 text-gray-600 font-semibold">Objetivos</th>
                  <th className="px-4 py-3 text-gray-600 font-semibold">Estado</th>
                  <th className="px-4 py-3 text-gray-600 font-semibold">Intentos</th>
                  <th className="px-4 py-3 text-gray-600 font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {results.map((r) => {
                  const passedObjs = (r.objective_results || []).filter(o => o.passed).length;
                  const totalObjs  = (r.objective_results || []).length;
                  const pct        = r.percentage != null ? Number(r.percentage).toFixed(0) : 0;
                  const isExpanded = expanded[r.student_id];
                  const isOpening  = openingFor[r.student_id];

                  return (
                    <React.Fragment key={r.student_id}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        {/* Rank */}
                        <td className="px-4 py-3 font-bold text-gray-400">
                          {r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : `#${r.rank}`}
                        </td>

                        {/* Alumno */}
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800">
                            {r.student_name || r.student_email || r.student_id}
                          </div>
                          {r.student_email && r.student_name && (
                            <div className="text-xs text-gray-400">{r.student_email}</div>
                          )}
                        </td>

                        {/* Nota */}
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-800">
                            {r.score}
                            <span className="text-gray-400 font-normal">/{maxScore}</span>
                          </div>
                          <div className="w-24 bg-gray-200 rounded-full h-1.5 mt-1">
                            <div
                              className={`h-1.5 rounded-full ${r.passed ? 'bg-green-500' : 'bg-yellow-400'}`}
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{pct}%</div>
                        </td>

                        {/* Objetivos */}
                        <td className="px-4 py-3">
                          <span className="text-green-600 font-semibold">{passedObjs}</span>
                          <span className="text-gray-400">/{totalObjs}</span>
                          {totalObjs > 0 && (
                            <button
                              onClick={() => toggleExpanded(r.student_id)}
                              className="ml-2 text-indigo-500 hover:text-indigo-700 text-xs underline"
                            >
                              {isExpanded ? 'ocultar' : 'ver detalle'}
                            </button>
                          )}
                        </td>

                        {/* Estado */}
                        <td className="px-4 py-3">
                          {r.passed ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                              ✅ Aprobado
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">
                              ⏳ En progreso
                            </span>
                          )}
                        </td>

                        {/* Intentos */}
                        <td className="px-4 py-3 text-gray-600">
                          {r.attempts || 1}
                          {r.improvement > 0 && (
                            <span className="ml-1 text-green-500 text-xs">+{r.improvement}pts</span>
                          )}
                        </td>

                        {/* Acciones */}
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => verifyStudentActivity(r.student_id, r.student_name || r.student_id)}
                              disabled={verifyingStudent[r.student_id]}
                              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-all disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                              title="Verificar progreso del alumno"
                            >
                              {verifyingStudent[r.student_id]
                                ? <><span className="animate-spin inline-block">⏳</span> Verificando...</>
                                : <><span>🔍</span> Verificar</>
                              }
                            </button>
                            <button
                              onClick={() => openStudentOdoo(r.student_id, r.student_name || r.student_id)}
                              disabled={isOpening}
                              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-lg transition-all disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                            >
                              {isOpening
                                ? <><span className="animate-spin inline-block">⏳</span> Abriendo...</>
                                : <><span>🏢</span> Abrir Odoo</>
                              }
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Fila expandida: detalle de objetivos */}
                      {isExpanded && (
                        <tr className="bg-indigo-50">
                          <td colSpan={7} className="px-6 py-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {(r.objective_results || []).map((obj, i) => (
                                <div
                                  key={i}
                                  className={`flex items-start gap-2 p-2 rounded-lg text-xs ${
                                    obj.passed
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-50 text-red-700'
                                  }`}
                                >
                                  <span className="mt-0.5 flex-shrink-0">{obj.passed ? '✅' : '❌'}</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium">{obj.name}</div>
                                    {!obj.passed && obj.message && (
                                      <div className="text-red-500 mt-0.5 opacity-80 truncate">
                                        {obj.message.replace(/^❌\s*[^-]+-\s*/, '')}
                                      </div>
                                    )}
                                  </div>
                                  <span className="font-semibold opacity-60 flex-shrink-0">{obj.score}pts</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Classroom Modal */}
        {showClassroomModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                🎓 Enviar calificaciones a Google Classroom
              </h2>
              
              {classroomMessage && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  classroomMessage.type === 'success' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {classroomMessage.text}
                </div>
              )}
              
              {classroomConnected === false ? (
                <div className="text-center py-6">
                  <div className="text-5xl mb-4">🔗</div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    Conecta Google Classroom
                  </h3>
                  <p className="text-gray-500 text-sm mb-4">
                    Necesitas conectar tu cuenta de Google Classroom desde el panel del profesor para poder enviar calificaciones.
                  </p>
                  <button
                    onClick={() => navigate('/teacher')}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    Ir al panel del profesor
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Curso de Classroom
                    </label>
                    <select
                      value={selectedCourse}
                      onChange={handleCourseChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Selecciona un curso...</option>
                      {courses.map(course => (
                        <option key={course.id} value={course.id}>
                          {course.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {selectedCourse && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tarea (Coursework)
                      </label>
                      <select
                        value={selectedCoursework}
                        onChange={(e) => setSelectedCoursework(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Selecciona una tarea...</option>
                        {courseworks.map(work => (
                          <option key={work.id} value={work.id}>
                            {work.title}
                          </option>
                        ))}
                      </select>
                      
                      <button
                        onClick={createClassroomAssignment}
                        disabled={creatingAssignment}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        {creatingAssignment ? 'Creando...' : '+ Crear nueva tarea en Classroom'}
                      </button>
                    </div>
                  )}
                  
                  <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
                    <p>📊 <strong>Resumen de calificaciones:</strong></p>
                    <p>• Total de alumnos: {stats?.total_students || 0}</p>
                    <p>• Se enviará la mejor nota de cada alumno</p>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 mt-6">
                {classroomConnected !== false && (
                  <button
                    onClick={submitAllGrades}
                    disabled={submittingGrades || !selectedCourse || !selectedCoursework}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submittingGrades ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="animate-spin">⏳</span>
                        Enviando...
                      </span>
                    ) : (
                      'Enviar todas las calificaciones'
                    )}
                  </button>
                )}
                <button
                  onClick={() => setShowClassroomModal(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-all"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="bg-white/10 rounded-xl p-4 border border-white/20 text-white/60 text-xs">
          💡 <strong className="text-white/80">"Verificar"</strong> comprueba el progreso actual del alumno en su contenedor Odoo.<br/>
          <strong className="text-white/80">"Abrir Odoo"</strong> inicia o reconecta el contenedor del alumno (igual que lo hace él).<br/>
          Los contenedores se apagan tras 30 min de inactividad pero los resultados persisten siempre.
          Se actualiza automáticamente cada 30 segundos.
        </div>

      </div>
    </div>
  );
}

export default OdooActivityMonitor;
